import axios, { AxiosInstance } from "axios";
import env from "../../helpers/environment";
import { applyKeycloakToken, retryWithRefreshedToken } from "../keycloakToken";
import { getUploadRejection } from '../../helpers/uploadRequestRejection';

export const API = axios.create({
  baseURL: env.apiHostUrl + "api/",
  headers: {
    "Content-type": "application/json",
  },
  withCredentials: false,
});

const attachAuthInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use((config) => applyKeycloakToken(config));

  client.interceptors.response.use(
    (response) => {
      const rejection = getUploadRejection(response);
      if (rejection) throw rejection;
      return response;
    },
    async (error) => {
      const rejection = getUploadRejection(error.response);
      if (rejection) return Promise.reject(rejection);
      if (error.response?.status === 401) {
        try {
          return await retryWithRefreshedToken(error, (config) => client.request(config));
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      }
      return Promise.reject(error);
    }
  );
};

attachAuthInterceptor(API);

export const FormDataAPI = axios.create({
  baseURL: env.apiHostUrl + "api/",
  headers: { "Content-Type": "multipart/form-data" },
  withCredentials: false,
});

attachAuthInterceptor(FormDataAPI);

interface DownloadTicketResponse {
  Ticket: string;
  Url: string;
  ExpiresAt: string;
}

function extractMessageFromBody(body: unknown): string | undefined {
  if (body == null) {
    return undefined;
  }
  if (typeof body === `string`) {
    const trimmed = body.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return extractMessageFromBody(parsed);
    } catch {
      return trimmed;
    }
  }
  if (typeof body === `object`) {
    const record = body as Record<string, unknown>;
    const candidates = [record.detail, record.title, record.Message, record.message, record.error];
    for (const candidate of candidates) {
      if (typeof candidate === `string` && candidate.trim()) {
        return candidate.trim();
      }
    }
  }
  return undefined;
}

async function parseDownloadError(error: unknown): Promise<string> {
  const axiosError = error as { response?: { data?: unknown } };
  if (axiosError?.response) {
    const { data } = axiosError.response;
    if (typeof Blob !== `undefined` && data instanceof Blob) {
      try {
        const text = await data.text();
        return extractMessageFromBody(text) || `Не удалось скачать файл`;
      } catch {
        // fall through
      }
    }
    if (typeof data === `string` && data.trim()) {
      return extractMessageFromBody(data) || data.trim();
    }
    const message = extractMessageFromBody(data);
    if (message) {
      return message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return `Не удалось скачать файл`;
}

export async function createDownloadTicket(apiRelativePath: string): Promise<string> {
  const path = apiRelativePath.replace(/^\//, ``);
  try {
    const response = await API.post<DownloadTicketResponse>(`Downloads/CreateTicket`, { Path: path });
    const url = response.data?.Url;
    if (!url) {
      throw new Error(`Не удалось получить ссылку для скачивания`);
    }
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    return `${env.apiHostUrl}${url.replace(/^\//, ``)}`;
  } catch (error) {
    throw new Error(await parseDownloadError(error));
  }
}

const DOWNLOAD_FRAME_TTL_MS = 120_000;
let downloadFrameSeq = 0;

function startHiddenFrameDownload(url: string): void {
  const name = `pda-download-${Date.now()}-${++downloadFrameSeq}`;
  const frame = document.createElement(`iframe`);
  frame.name = name;
  frame.id = name;
  frame.setAttribute(`aria-hidden`, `true`);
  frame.style.cssText = `position:absolute;width:0;height:0;border:0;visibility:hidden`;
  document.body.appendChild(frame);

  const link = document.createElement(`a`);
  link.href = url;
  link.target = name;
  link.rel = `noreferrer`;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => frame.remove(), DOWNLOAD_FRAME_TTL_MS);
}

export function navigateToDownloadUrl(url: string): void {
  startHiddenFrameDownload(url);
}

type DownloadCheckRule = {
  match: RegExp;
  toCheckPath: (match: RegExpMatchArray) => string;
};

const DOWNLOAD_CHECK_RULES: DownloadCheckRule[] = [
  {
    match: /^Attachments\/Download\?id=([^&]+)/i,
    toCheckPath: (m) => `Attachments/CheckDownload?id=${m[1]}`,
  },
  {
    match: /^Attachments\/GetById\?id=([^&]+)/i,
    toCheckPath: (m) => `Attachments/CheckGetById?id=${m[1]}`,
  },
  {
    match: /^Attachments\/DownloadArchiveByWorkflowInstance\?id=(\d+)/i,
    toCheckPath: (m) => `Attachments/CheckDownloadArchiveByWorkflowInstance?id=${m[1]}`,
  },
];


async function ensureDownloadAvailable(apiRelativePath: string): Promise<void> {
  const path = apiRelativePath.replace(/^\//, ``);
  const rule = DOWNLOAD_CHECK_RULES.find((item) => item.match.test(path));
  if (!rule) {
    return;
  }

  const match = path.match(rule.match);
  if (!match) {
    return;
  }

  try {
    await API.get(rule.toCheckPath(match));
  } catch (error) {
    throw new Error(await parseDownloadError(error));
  }
}

async function downloadExportAsBlob(apiRelativePath: string): Promise<void> {
  const path = apiRelativePath.replace(/^\//, ``);
  try {
    const response = await API.get<Blob>(path, { responseType: `blob` });
    const contentType = `${response.headers[`content-type`] ?? ``}`.toLowerCase();
    if (contentType.includes(`application/json`) || contentType.includes(`text/html`) || contentType.includes(`text/plain`)) {
      const text = await response.data.text();
      throw new Error(extractMessageFromBody(text) || `Не удалось скачать файл`);
    }
    const disposition = `${response.headers[`content-disposition`] ?? ``}`;
    const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1].replace(/"/g, ``)) : `download`;
    const objectUrl = window.URL.createObjectURL(response.data);
    const tempLink = document.createElement(`a`);
    tempLink.href = objectUrl;
    tempLink.setAttribute(`download`, fileName);
    tempLink.click();
    window.URL.revokeObjectURL(objectUrl);
    tempLink.remove();
  } catch (error) {
    throw new Error(await parseDownloadError(error));
  }
}

export async function startNativeDownload(apiRelativePath: string): Promise<void> {
  const path = apiRelativePath.replace(/^\//, ``);
  await ensureDownloadAvailable(path);

  // Acts Export: iframe глотает 4xx/5xx — качаем blob через axios, ошибка уходит в toast.
  if (/^Acts\/Export\?/i.test(path) || /^ActTemplates\/Export\?/i.test(path)) {
    await downloadExportAsBlob(path);
    return;
  }

  const url = await createDownloadTicket(path);
  navigateToDownloadUrl(url);
}

export async function triggerDownload(
  apiRelativePath: string,
  _fallbackFileName?: string
): Promise<void> {
  await startNativeDownload(apiRelativePath);
}

